<template>
  <div class="filter-panel" :class="direction">
    <div v-if="filtersIconEnabled" class="filter-panel__icon">
      <i class="fa fa-2x" :class="filterIconClass"></i>
    </div>

    <div class="filter-panel__groups">
      <div class="filter-group"
           :class="{'with-title': !!group.title}"
           v-for="(group, groupIndex) in filterGroups"
           :key="`group-${groupIndex}`">
        <div v-if="group.title" class="filter-group__title">
          {{ group.title }}
        </div>

        <div v-for="(field, fieldIndex) in group.items" :key="`field-${fieldIndex}`" class="field-container">

          <!-- checkbox -->
          <b-form-checkbox
            v-if="field.type === 'checkbox'"
            class="filter-field filter-field__checkbox"
            @input="applyFilter"
            v-model="filterValues[field.name]"
            :disabled="apiRequestIsPerforming">
            {{ field.label }}
          </b-form-checkbox>

          <!-- switch -->
          <div v-if="field.type === 'switch'" class="filter-field filter-field__switch">
            <CSwitch :checked="filterValues[field.name]"
                     @update:checked=" value => {filterValues[field.name] = value; applyFilter();}"
                     v-bind="{labelOn: 'on', labelOff: 'off'}"
                     class="switch" color="dark" variant="outline" shape="pill" size="sm"/>
            <div class="label">
              {{ field.label }}
            </div>
          </div>

          <!-- field: date range custom implementation -->
          <date-range
            v-if="field.type === 'daterange'"
            class="filter-field filter-field__daterange"
            @input="applyFilter"
            :startDate.sync="filterValues[field.name[0]]"
            :endDate.sync="filterValues[field.name[1]]"
            :disabled="apiRequestIsPerforming"/>

          <!-- field: date range with "Vue2 date range picker" plugin -->
          <date-range-picker-custom
            v-if="field.type === 'daterange-picker'"
            class="filter-field filter-field__daterange-picker"
            @input="applyFilter"
            :options="field.options"
            :label="field.label"
            :startDate.sync="filterValues[field.name[0]]"
            :endDate.sync="filterValues[field.name[1]]"
            :disabled="apiRequestIsPerforming"/>

          <!-- field: select -->
          <b-form-group v-if="field.type === 'select'"
                        :label="field.label"
                        :label-for="field.label ? field.name : undefined"
                        :label-cols="field.label ? 5 : undefined"
                        class="filter-field filter-field__select">
            <b-form-select :id="field.name"
                           @input="applyFilter"
                           v-model="filterValues[field.name]"
                           :options="field.choices"
                           :disabled="apiRequestIsPerforming"/>
          </b-form-group>

          <!-- field: select-buttons -->
          <b-form-group v-if="field.type === 'select-buttons'"
                        class="filter-field filter-field__select-buttons choice-buttons">
            <span v-if="field.label" class="label">
              {{ field.label }}
            </span>

            <template v-for="item in field.choices">
              <b-button :key="`location-${item.value}`"
                        :pressed="filterValues[field.name] === item.value"
                        :disabled="apiRequestIsPerforming"
                        @click="() => { filterValues[field.name] = item.value; applyFilter() }"
                        class="choice-button"
                        variant="primary"
                        size="sm">
                {{ item.text }}
              </b-button>
            </template>
          </b-form-group>

          <!-- field: multiselect / multiselect-region / multiselect-direction -->
          <b-form-group v-if="field.type.startsWith('multiselect')"
                        class="filter-field filter-field__multiselect">
            <multiselect
              :ref="`${field.name}_multiselect`"
              @select="handleMultiselectSelect(field, ...arguments)"
              @remove="handleMultiselectRemove(field, ...arguments)"
              @close="handleMultiselectClose(field, ...arguments)"
              v-model="filterValues[field.name]"
              :options="field.choices"

              :group-label="getNested(field, 'options.groups.label', null)"
              :group-values="getNested(field, 'options.groups.values', null)"
              :group-select="!!getNested(field, 'options.groups')"

              :disabled="apiRequestIsPerforming"
              :multiple="getNested(field, 'options.multiple', true)"
              :allow-empty="getNested(field, 'options.multiple', true)"
              :close-on-select="getNested(field, 'options.closeOnSelect', false)"
              :limit="2"
              :searchable="true"
              label="text"
              selectedLabel=""
              track-by="text"
              :placeholder="field.placeholder">

              <template v-if="multiselectBeforeListAvailable(field)">
                <div slot="beforeList">
                  <!-- map selector (first item in the dropdown) -->
                  <multiselect-region-selector
                    v-if="field.type === 'multiselect-region'"
                    @input="applyFilter"
                    :all-regions="field.choices"
                    :selected-regions.sync="filterValues[field.name]"/>

                  <!-- direction selector (first item in the dropdown) -->
                  <multiselect-direction-selector
                    v-if="field.type === 'multiselect-direction'"
                    @input="applyFilter"
                    :all-directions="field.choices"
                    :selected-directions.sync="filterValues[field.name]"/>

                  <!-- toggle all/none selector, could be attached to any type of multiselect -->
                  <multiselect-toggle-all-selector
                    v-if="getNested(field, 'options.toggleAll', false)"
                    @select-all="handleMultiselectSelectAll(field)"
                    @select-none="handleMultiselectSelectNone(field)"/>
                </div>
              </template>

              <!-- multiselect list items -->
              <template #singleLabel="{option}" class="list-item">
                <div>
                  <strong>{{ option.text }}</strong>
                </div>
              </template>

              <template #option="{option}">
                <!-- group option -->
                <div v-if="option.$isLabel" class="group-option">
                  {{ option.$groupLabel }}
                </div>

                <!-- regular option -->
                <div v-else  class="regular-option">
                  <div>
                    <small>{{ option.text }}</small>
                  </div>
                  <div v-if="getNested(field, 'options.valueLabel') !== false">
                    <small v-if="typeof getNested(field, 'options.valueLabel') === 'string'">
                      {{ option[field.options.valueLabel] || '--' }}
                    </small>
                    <small v-else>
                      {{ option.value }}
                    </small>
                  </div>
                </div>
              </template>
            </multiselect>
          </b-form-group>

          <!-- treeselect -->
          <b-form-group v-if="field.type === 'treeselect'" class="filter-field filter-field__treeselect">
            <treeselect
              @close="handleTreeselectClose(field, ...arguments)"
              @input="handleTreeselectInput(field, ...arguments)"
              :ref="`${field.name}_treeselect`"
              v-model="filterValues[field.name]"
              class="vue-treeselect-custom"
              :disabled="apiRequestIsPerforming"
              :placeholder="field.placeholder"
              :options="field.choices"
              :multiple="true"
              :limit="1"
              :search-nested="true"
              :always-open="false"
              :open-on-focus="true"
              :open-on-click="false"
              value-format="object"
              value-consists-of="LEAF_PRIORITY">

              <template #before-list>
                <!-- toggle all/none selector, treeselect version -->
                <treeselect-toggle-all-selector
                  v-if="getNested(field, 'options.toggleAll', false)"
                  @select-all="handleTreeselectSelectAll(field)"
                  @select-none="handleTreeselectSelectNone(field)"/>
              </template>
            </treeselect>
          </b-form-group>

          <!-- range field -->
          <div v-if="field.type === 'range'" class="filter-field filter-field__range">
            <div class="label">
              <formatted-number-input
                :disabled="apiRequestIsPerforming"
                v-model="filterValues[field.name][0]"
                @input="applyFilter"
                :min="field.min"
                :max="field.max"
                placeholder="Min"
                size="sm" />

              {{ field.label }}

              <formatted-number-input
                :disabled="apiRequestIsPerforming"
                v-model="filterValues[field.name][1]"
                @input="applyFilter"
                :min="field.min"
                :max="field.max"
                placeholder="Max"
                size="sm" />
            </div>

            <vue-slider
              :disabled="apiRequestIsPerforming"
              class="slider"
              v-model="filterValues[field.name]"
              @change="applyFilter"
              :tooltip-formatter="value => rangeTooltipFormatter(field, value)"
              :min="field.min"
              :max="field.max"
              :contained="true"
              :lazy="true"
              tooltipPlacement="top"/>
          </div>

        </div>
      </div>
    </div>

    <!-- request parameters -->
    <div v-if="apiRequestData || panelActionsAvailable" class="filter-panel__request-management">
      <api-request-details v-if="apiRequestData" :api-request-data="apiRequestData"/>

      <slot name="panel-actions">
        <!-- refresh filter button for example -->
      </slot>
    </div>

    <!-- notification text -->
    <div v-if="notificationText" class="filter-panel__notification">
      <i>{{ notificationText }}</i>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce'
import isEqual from 'lodash-es/isEqual'
import isEmpty from 'lodash-es/isEmpty'
import getNested from 'lodash-es/get'
import cloneDeep from 'lodash-es/cloneDeep'

import { mapActions, mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import Treeselect from '@riophae/vue-treeselect'

import ApiRequestDetails from './APIRequestDetails'
import MultiselectRegionSelector from '../widgets/MultiselectRegionSelector'
import MultiselectDirectionSelector from '../widgets/MultiselectDirectionSelector'
import DateRange from '../widgets/DateRange'
import FormattedNumberInput from './FormattedNumberInput'
import DateRangePickerCustom from './DateRangePickerCustom'
import MultiselectToggleAllSelector from './MultiselectToggleAllSelector'
import TreeselectToggleAllSelector from './TreeselectToggleAllSelector'
import { formatVolume, formatPercent } from '@/shared/number-utils'


// settings
const debounceDelay = 150;


export default {
  name: 'filter-panel-new',

  components: {
    FormattedNumberInput,
    ApiRequestDetails,
    Multiselect,
    MultiselectRegionSelector,
    MultiselectDirectionSelector,
    MultiselectToggleAllSelector,
    Treeselect,
    TreeselectToggleAllSelector,
    DateRange,
    DateRangePickerCustom,
  },

  props: {
    filterGroups: {
      type: Array,
      default: () => [],
    },

    direction: {
      type: String,
      default: 'horizontal',  // "horizontal" / "vertical"
    },

    apiRequestData: {
      type: Object,
      default: null,
    },

    notificationText: {
      type: String,
      default: null,
    },

    // flags
    applyFilterImmediately: {
      type: Boolean,
      default: true,
    },

    trackFilterSettings: {
      type: Boolean,
      default: true,
    },

    noPaddings: {
      type: Boolean,
      default: false,
    },

    filtersIconEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      apiRequestIsPerforming: false,
      delayedUpdate: {
        // used in multiselect when dropdown is opened to prevent immediate updates
        // {[field.name]: true}
      },
      filterValues: {},
      previousFilterValues: {},
    };
  },

  mounted() {
    if(this.applyFilterImmediately) {
      this.applyFilter();
    }
  },

  methods: {
    getNested: getNested,

    ...mapActions('api/presets', [
      'updateCurrentFilterSettings',
    ]),

    getFilterValues(filterGroups) {
      let filterValues = {};
      for(let group of filterGroups) {
        for(let field of group.items) {
          if(['daterange', 'daterange-picker'].includes(field.type)) {
            // composite field "daterange", 2 values in one field
            filterValues[field.name[0]] = field[field.name[0]].value;
            filterValues[field.name[1]] = field[field.name[1]].value;
            continue;
          }
          filterValues[field.name] = field.value;
        }
      }

      // we need a new object to prevent issues with watch and values updating
      return cloneDeep(filterValues);
    },

    applyFilterDebounced: debounce(function () {
      this.applyFilter();
    }, debounceDelay),

    async applyFilter() {
      // filter is applied when:
      // - internal filter-panel values are changed (this.filterValues)
      // - props are received and these props have new field values (to provide predefined values for inputs)

      const currentValues = this.filterValues;
      const oldValues = this.previousFilterValues;

      if(!isEqual(currentValues, oldValues)) {
        this.previousFilterValues = cloneDeep(this.filterValues);  // it's previous for the next time
        // console.log('[filter-panel] applyFilter, internal values changed');
        await this.trackFilterSettingsToStore();  // for saved preferences
        this.$emit('update', cloneDeep(this.filterValues));
        return;
      }

      const newValues = this.getFilterValues(this.filterGroups);
      if(!isEqual(newValues, oldValues)) {
        this.previousFilterValues = cloneDeep(newValues);  // it's previous for the next time
        this.filterValues = newValues;
        // console.log('[filter-panel] applyFilter, props changed');
        await this.trackFilterSettingsToStore();  // for saved preferences
        this.$emit('update', cloneDeep(this.filterValues));
      }
    },

    async trackFilterSettingsToStore() {
      if(this.trackFilterSettings) {
        // console.log('trackFilterSettingsToStore');
        await this.updateCurrentFilterSettings({
          path: this.$route.path,
          parameters: {
            // filterPanel: cleanupFilterValues(this.filterValues),
            filterPanel: this.filterValues,
            // in case of complex filters it's needed to have additional sections,
            // for example:
            // searchPanel: { ..search values, etc.. }
          },
        });
      }
    },

    // special cases
    // multiselect
    multiselectBeforeListAvailable(field) {
      const beforeListFields = ['multiselect-direction', 'multiselect-region'];
      return (
        beforeListFields.includes(field.type) ||
        getNested(field, 'options.toggleAll', false)
      );
    },

    handleMultiselectSelectAll(field) {
      this.filterValues[field.name] = cloneDeep(field.choices);
      this.delayedUpdate[field.name] = true;
    },

    handleMultiselectSelectNone(field) {
      this.filterValues[field.name] = [];
      this.delayedUpdate[field.name] = true;
    },

    handleMultiselectClose(field) {
      if(this.delayedUpdate[field.name]) {
        this.applyFilter();
        this.delayedUpdate[field.name] = false;
      }
    },

    handleMultiselectSelect(field) {
      // $refs[name] is an array since we assign it within a "v-for"
      const multiselectObj = this.$refs[`${field.name}_multiselect`][0];

      if(multiselectObj.isOpen) {
        // if multiselect opened value will be applied on @close event
        // this way we prevent to close dropdown and allow adding a few elements at once
        this.delayedUpdate[field.name] = true;
      }
      // multiselect - select is possible only when multiselect is open,
      // there is no need to applyFilter immediately
    },

    handleMultiselectRemove(field) {
      // $refs[name] is an array since we assign it within a "v-for"
      const multiselectObj = this.$refs[`${field.name}_multiselect`][0];

      if(multiselectObj.isOpen) {
        // if multiselect opened value will be applied on @close event
        // this way we prevent to close dropdown and allow removing a few elements at once
        this.delayedUpdate[field.name] = true;
      } else {
        // if multiselect is closed it's needed to apply filter immediately
        this.$nextTick(() => {
          // since multiselect @remove emits before actual value changed, nextTick is required here
          this.applyFilter();
        });
      }
    },

    // treeselect
    handleTreeselectClose() {
      // parameters: field, selectedValues, $$0
      // we don't need to handle delayer updates here,
      // since treeselect auto-opens on item removing, so close() will be fired automatically
      this.applyFilter();
    },

    handleTreeselectInput(field, value) {
      const treeselectObj = this.$refs[`${field.name}_treeselect`][0];
      const isOpen = treeselectObj.$data.menu.isOpen;

      if(!isOpen && isEmpty(value)) {
        this.applyFilter();
      }
    },

    handleTreeselectSelectAll(field) {
      let subItemChoices = [];
      field.choices.forEach(item => {
        subItemChoices = subItemChoices.concat(cloneDeep(item.children));
      });

      this.filterValues[field.name] = subItemChoices;
    },

    handleTreeselectSelectNone(field) {
      this.filterValues[field.name] = [];
    },

    // slider
    rangeTooltipFormatter(field, value) {
      if(value > 10000) {
        return formatVolume(value);
      }
      if(field.min === 0 && field.max === 100) {
        return formatPercent(value / 100);
      }
      return value;
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),

    panelActionsAvailable() {
      return !!this.$slots['panel-actions'];
    },

    filterIconClass: function () {
      if(this.apiRequestIsPerforming) {
        return 'fa-cog fa-spin';
      } else {
        return 'fa-filter';
      }
    },
  },

  watch: {
    getActiveRequestsCount: debounce(function (newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay),

    filterGroups: {
      immediate: true,
      deep: true,
      handler: function () {
        this.applyFilter()
      },
    },
  },
}


function getFormattedFilters(filterSchema, requiredFields) {
  // generates filters by filterSchema which is used in <filter-panel>
  const fieldNames = requiredFields ? requiredFields : Object.keys(filterSchema);

  const group = fieldNames.map((fieldName) => {
    return filterSchema[fieldName];
  });
  return {
    groups: [group],
  };
}

function getCurrentAPIFilterValues(filterSchema) {
  let apiFilterValues = {};
  Object.keys(filterSchema).forEach(fieldName => {
    let field = filterSchema[fieldName];
    let value = filterSchema[fieldName].value;

    if(['daterange', 'daterange-picker'].includes(field.type)) {
      // composite field 2 values in one
      let dateStartName = field.name[0];
      let dateEndName = field.name[1];
      apiFilterValues[field[dateStartName].apiName] = filterSchema[fieldName][dateStartName].value;
      apiFilterValues[field[dateEndName].apiName] = filterSchema[fieldName][dateEndName].value;
      return;
    }

    if(field.type === 'range') {
      // prevent to extract ".value" like in select/multiselect
      apiFilterValues[field.apiName] = cloneDeep(value);
      return;
    }

    if(field.type === 'treeselect') {
      apiFilterValues[field.apiName] = value.map(item => item.id);
      return;
    }

    // single and multiple choices (list of objects with .value attribute) are possible here
    if(Array.isArray(value)) {
      apiFilterValues[field.apiName] = value.map(item => item.value);
    } else {
      // could be a plain value, or an object {value: <value>, text: <text>}
      apiFilterValues[field.apiName] = getNested(value, 'value', value);
    }
  });

  return apiFilterValues;
}

function getCurrentFilterValues(filterSchema) {
  // generates object for applyFilterValues method, like:
  // applyFilterValues(filterSchema, <filterValues>)

  let filterValues = {};
  Object.keys(filterSchema).forEach(fieldName => {
    let field = filterSchema[fieldName];
    let value = filterSchema[fieldName].value;

    if(field.type === 'daterange') {
      // composite field 2 values in one
      let dateStartName = field.name[0];
      let dateEndName = field.name[1];
      filterValues[field[dateStartName].name] = field[dateStartName];
      filterValues[field[dateEndName].name] = field[dateEndName];
    } else if(field.type === 'daterange-picker') {
      let dateStartName = field.name[0];
      let dateEndName = field.name[1];
      filterValues[field[dateStartName].name] = field[dateStartName].value;
      filterValues[field[dateEndName].name] = field[dateEndName].value;
    } else {
      // single and multiple choices (list of objects with .value attribute) are possible here
      filterValues[field.name] = value;
    }
  });

  return filterValues;
}

function getAPIFilterValues(filterSchema, filterValues) {
  // generates an API-ready object (.apiName attribute is required in field objects)

  let apiFilterValues = {};

  Object.keys(filterSchema).forEach(fieldName => {
    let field = filterSchema[fieldName];
    let value = filterValues[fieldName];

    if(['daterange', 'daterange-picker'].includes(field.type)) {
      // composite field 2 values in one
      let dateStartName = field.name[0];
      let dateEndName = field.name[1];
      apiFilterValues[field[dateStartName].apiName] = filterValues[dateStartName];
      apiFilterValues[field[dateEndName].apiName] = filterValues[dateEndName];
      return;
    }

    if(field.type === 'treeselect') {
      apiFilterValues[field.apiName] = value.map(item => item.id);
      return;
    }

    // single and multiple choices (list of objects with .value attribute) are possible here
    if(Array.isArray(value)) {
      apiFilterValues[field.apiName] = value.map(item => item.value);
    } else {
      // could be a plain value, or an object {value: <value>, text: <text>}
      apiFilterValues[field.apiName] = getNested(value, 'value', value);
    }
  });

  return apiFilterValues;
}

function applyFilterValues(filterSchema, filterValues, requiredFields) {
  // note: this method updates filterSchema inplace!
  // if "requiredFields" are specified, only these fields will be updated
  const fieldNames = requiredFields ? requiredFields : Object.keys(filterSchema);

  fieldNames.forEach(fieldName => {
    let field = filterSchema[fieldName];
    let value = filterValues[fieldName];

    if(['daterange', 'daterange-picker'].includes(field.type)) {
      let dateStartName = field.name[0];
      let dateEndName = field.name[1];
      field[dateStartName].value = filterValues[dateStartName];
      field[dateEndName].value = filterValues[dateEndName];
    } else {
      field.value = value;
    }
  });
}

function cleanupFilterValues(filterValues) {
  // removes non required values in various fields

  let filterValuesCopy = cloneDeep(filterValues);

  // keep only these fieldNames in choice fields: [{}, ..]
  const choicesFieldNames = ['value', 'text', 'id', 'label'];

  Object.keys(filterValuesCopy).forEach((fieldName) => {
    let section = filterValuesCopy[fieldName];
    if(Array.isArray(section)) {
      section.forEach(item => {
        Object.keys(item).forEach(fieldName => {
          if(!choicesFieldNames.includes(fieldName)) {
            item[fieldName] = undefined;
          }
        });
      });
    }
  });

  return filterValuesCopy;
}

export {
  getCurrentFilterValues,
  getCurrentAPIFilterValues,
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters,
  cleanupFilterValues,
}
</script>

<style scoped>
  /*
    styles are here:
    assets/scss/widgets/filters_panel.scss
  */
</style>
